<template>
  <div class="detail-page">
    <div class="header">
      <button class="back-btn" @click="router.back()">Back</button>
      <h1>{{ game?.title }}</h1>
    </div>

    <div class="content" v-if="game">
      <!-- Game showcase -->
      <div class="game-showcase">
        <div class="main-image">
          <img :src="game.thumbnail" :alt="game.title">
        </div>
        <div class="thumbnail-list">
          <img 
            v-for="(img, index) in game.screenshots" 
            :key="index"
            :src="img"
            :alt="`${game.title} screenshot ${index + 1}`"
            @click="currentImage = img"
          >
        </div>
      </div>

      <!-- Game info -->
      <div class="game-info">
        <div class="info-section">
          <h2>Game Description</h2>
          <p>{{ game.description }}</p>
        </div>

        <div class="info-section">
          <h2>How to Play</h2>
          <p>{{ game.instructions }}</p>
        </div>

        <div class="action-buttons">
          <button class="play-btn" @click="playGame">Play Now</button>
        </div>
      </div>

      <!-- Comments section -->
      <div class="comments-section">
        <h2>Player Reviews</h2>
        <div class="rating">
          <span v-for="i in 5" :key="i" 
                :class="{ 'star-filled': i <= game.rating }">★</span>
        </div>
        
        <div class="comment-form">
          <textarea 
            v-model="newComment" 
            placeholder="Write your comment..."
            rows="3"
          ></textarea>
          <button @click="submitComment">Post Comment</button>
        </div>

        <div class="comments-list">
          <div v-for="comment in game.comments" :key="comment.id" class="comment">
            <div class="comment-header">
              <span class="username">{{ comment.username }}</span>
              <span class="date">{{ comment.date }}</span>
            </div>
            <p class="comment-content">{{ comment.content }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGameStore } from '../stores/gameStore'

const route = useRoute()
const router = useRouter()
const store = useGameStore()

const game = ref(null)
const currentImage = ref('')
const newComment = ref('')

onMounted(async () => {
  const gameId = parseInt(route.params.id)
  // Try to get game data from store first
  game.value = store.getGameById(gameId)
  
  // If store has no data, load it
  if (!game.value) {
    await store.loadGames()
    game.value = store.getGameById(gameId)
  }
  
  if (game.value) {
    currentImage.value = game.value.thumbnail
  }
})

const playGame = () => {
  router.push(`/play/${game.value.id}`)
}

const submitComment = () => {
  if (!newComment.value.trim()) return
  
  const comment = {
    id: Date.now(),
    username: 'Guest',
    content: newComment.value,
    date: new Date().toLocaleDateString()
  }
  
  game.value.comments.push(comment)
  newComment.value = ''
}
</script>

<style scoped>
.detail-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
}

.back-btn {
  padding: 8px 16px;
  background: #1E90FF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 20px;
}

.content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.game-showcase {
  grid-column: 1 / -1;
}

.main-image {
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-list {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 10px 0;
}

.thumbnail-list img {
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.thumbnail-list img:hover {
  transform: scale(1.05);
}

.info-section {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.action-buttons {
  text-align: center;
  margin-top: 30px;
}

.play-btn {
  padding: 12px 30px;
  background: #9370DB;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.play-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.comments-section {
  grid-column: 1 / -1;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.rating {
  margin: 20px 0;
}

.star-filled {
  color: #FFD700;
}

.comment-form {
  margin: 20px 0;
}

.comment-form textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
  resize: vertical;
}

.comment-form button {
  padding: 8px 16px;
  background: #1E90FF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.comments-list {
  margin-top: 20px;
}

.comment {
  padding: 15px;
  border-bottom: 1px solid #eee;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #666;
}

.comment-content {
  color: #333;
  line-height: 1.5;
}
</style> 